import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "rsuite";
import { FilterConfig, IDemandPayload, IRoot } from "../../services/interface/IDemand";
import { IDemandFutureForecastData, IDemandHistoricalData, IDemandVSForecast, IDemandVSForecastDeep } from "../../services/interface/IDemandForecast";
import BarGraph from "../../components/DemandBar/barGraph";
import DemandFilter from "../../components/Filter/demandForecastFilter";
import LineGraph from "../../components/DemandLine/lineGraph";
import { useGetFutureForecastDeepDiveData, useGetHistoricalForecastData } from "../../services/redux/hooks/useDeepdive";
import { setApplyFilter, setDBFilter, setSelectedDBFilter } from "../../services/redux/reducer/demandfilter.reducer";
import { useForecastOverViewDropDownFilter } from "../../services/redux/hooks/useDemand";
import CustomLoader from "../../common/CustomLoader/CustomLoader";

const ForecastDeepDive = () => {
    const initialState: IDemandPayload = {
        dc: [],
        storeNumber: [],
        itemId: [],
        fcwNumber: ["3"],
        ucode: [],
        productSegm: [],
        productDesc: [],
        category: [],
        subCategory: [],
        vendorName: [],
        modelType: ["error based best model"],
        adType: [],
    }
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [lastUpdated, setLastUpdated] = useState("");
    const [historicalData, setHistoricalData] = useState<IDemandHistoricalData[]>([]);
    const [futureData, setFutureData] = useState<IDemandFutureForecastData[]>([]);
    const [demandForecastData, setDemandForecastData] = useState<IDemandVSForecast[]>();
    //filter api data
    const [filter, setFilter] = useState<IDemandPayload>(initialState); 
    //selected fiters data
    const [selectedFilterData, setSelectedFilterData] = useState<IDemandPayload>(initialState);
    const applyFilter = useSelector((state:IRoot)=>state.dbfilter.applyFilter);
    // selected filter data - redux
    const selectedDbFilter = useSelector((state:IRoot)=>state.dbfilter.selectedDbFilter);
    const {mutate} = useGetHistoricalForecastData();
    const {mutate: mutateFutureDemand} = useGetFutureForecastDeepDiveData();
    const {mutate: mutateDropDown, data: resFilterData} = useForecastOverViewDropDownFilter();

    // Filter - Start
    const filters: FilterConfig[] = [
        { title: 'DC', key: 'dc', placeholder: 'All' },
        { title: 'Retail Store', key: 'storeNumber', placeholder: 'All' },
        { title: 'Category', key: 'category', placeholder: 'All' },
        { title: 'SubCategory', key: 'subCategory', placeholder: 'All' },
        { title: 'Segmentation', key: 'productSegm', placeholder: 'All' },
        { title: 'Product Desc.', key: 'productDesc', placeholder: 'All' },
        { title: 'Item Id', key: 'itemId', placeholder: 'All' },
        { title: 'Forecast Week', key: 'fcwNumber', placeholder: '3', isSelectPicker: true },
        { title: 'Vendor', key: 'vendorName', placeholder: 'All' },
        { title: 'Model Type', key: 'modelType', placeholder: 'All', isSelectPicker: false },
        { title: 'Ad Type', key: 'adType', placeholder: 'All' }
    ];  

    const filterHandler=async()=>{
        setLoading(true);
        try{
            await mutateDropDown(selectedFilterData);
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }
    }
    
    const setFilterData=()=>{
        if(resFilterData?.data){
            setFilter(resFilterData?.data);
            dispatch(setDBFilter(resFilterData?.data));
        }
    }

    const filterSelectionHandler=(key:any,value:any)=>{
        setSelectedFilterData({
            ...selectedFilterData,
            [key]:value
        });
    }

    const removeSelectionHandler=()=>{
        setSelectedFilterData(initialState);
    }
    // Filter - End
    
    const prepareForecastData=(data:any)=>{
        if(data){
            let finalData:IDemandHistoricalData[]=[];
            let futureFinalData:IDemandFutureForecastData[]=[];
            Array.isArray(data?.actualVsForecast) && 
            data?.actualVsForecast.map((each:IDemandHistoricalData,index:number)=>{
                let obj:IDemandHistoricalData = {
                    actuals: each?.actuals ?? 0,
                    adImpact: each?.adImpact ?? 0,
                    baseForecast: each?.baseForecast ?? 0,
                    forecastDate: moment(each?.forecastDate?.slice(0, 10)).add(2, 'years').format("MM-DD-YY"),
                    priceForecast: each?.priceForecast ?? 0
                }
                finalData.push(obj);
            });
            Array.isArray(data?.futureforecast) &&
            data?.futureforecast.map((each: IDemandFutureForecastData ,index: number)=>{
                let obj:IDemandFutureForecastData = {
                    adImpact: each?.adImpact ?? 0,
                    baseForecast: each?.baseForecast ?? 0,
                    forecastDate: moment(each?.forecastDate?.slice(0, 10)).add(2, 'years').format("MM-DD-YY"),
                    priceForecast: each?.priceForecast ?? 0
                }
                futureFinalData.push(obj);
            })
            setHistoricalData(finalData);
            setFutureData(futureFinalData);
        }
    }

    const prepareDemandForecast=(data:any)=>{
        if(data){
            let finalData:IDemandVSForecast[]=[];
            Array.isArray(data) && 
            data?.map((each:IDemandVSForecastDeep,index:number)=>{
                let obj = {
                    actuals: each?.TotalPounds ?? 0,
                    forecastDate: moment(each?.forecast_date?.slice(0, 10)).add(2, 'years').format("MM-DD-YY"),
                    totalForecast: each?.TotalForecast ?? 0
                }
                finalData.push(obj);
            });
            setDemandForecastData(finalData);
        }
    }

    const getHistoricalData=async(payload:IDemandPayload)=>{
        try{
            setLoading(true);
            const res = await mutate(payload);
            if(res?.data){
                prepareForecastData(res?.data?.data);
            }
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }
    }

    const getDemandVsForecast=async(payload:IDemandPayload)=>{
        try{
            setLoading(true);
            const res = await mutateFutureDemand(payload);
            if(res?.data){
                prepareDemandForecast(res?.data?.data?.futureData);
                setLastUpdated(res?.data?.data?.lastUpdateDate);
            }
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }
    }

    useEffect(()=>{
        dispatch(setSelectedDBFilter(selectedFilterData));
    },[selectedFilterData]);

    useEffect(()=>{
        setFilterData();
        return ()=>{
            dispatch(setSelectedDBFilter(initialState));
        }
    },[resFilterData?.data]);

    useEffect(()=>{
        if(applyFilter === ""){
            getHistoricalData(initialState);
            getDemandVsForecast(initialState);
        }
        else if(applyFilter === "applied"){
            getHistoricalData(selectedDbFilter);
            getDemandVsForecast(selectedDbFilter);
        }
    },[applyFilter, dispatch]);

    useEffect(()=>{
        return ()=>{
            dispatch(setApplyFilter(""));
            dispatch(setSelectedDBFilter(initialState));
        }
    },[dispatch]);

    return (
        <>
            <div className="relative">
                <DemandFilter
                    selectedFilterData={selectedFilterData}
                    filterSelectionHandler={filterSelectionHandler}
                    removeSelectionHandler={removeSelectionHandler}
                    resFilterData={filter}
                    filters={filters}
                    filterHandler={filterHandler}
                    alertDesc="Demand Forecasting > Forecast Deepdive" 
                    lastUpdated={lastUpdated} />
            </div>
            <div className="bg-[#F1F1F1] h-[36vh] px-4 pt-3 pb-[0]">
                <LineGraph 
                    title={"Demand vs Forecast"} 
                    dataName={["Actuals","Total Forecast"]}
                    dataKey={["actuals","totalForecast"]}
                    data={demandForecastData ?? []} />
            </div>
            <div className="bg-[#F1F1F1] h-[36vh] px-4 py-2">
                <div className="grid grid-cols-2 gap-2 h-full">
                    <BarGraph 
                        flag={1} 
                        title={"Historical Demand vs forecast"} 
                        dataKey={["baseForecast","priceForecast","adImpact","actuals"]}
                        dataName={["Base Forecast","Price Forecast","AD Impact", "Actuals"]}
                        data={historicalData ?? []} />
                    <BarGraph 
                        flag={2} 
                        title={"Future Forecasts"} 
                        dataKey={["baseForecast","priceForecast","adImpact"]}
                        dataName={["Base Forecast","Price Forecast","AD Impact"]}
                        data={futureData ?? []} />
                </div>
            </div>
            {loading && <CustomLoader open={loading} />}
        </>
    );
}

export default ForecastDeepDive;
